import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, ActivityIndicator, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Location from 'expo-location';
import { Header, ScreenLayout, FormHeader, colors, spacing, typography } from '../components';
import { withAlpha } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, Visit } from '../services/api';
import { buildMapsUrl, MapsDirectionsMode } from '../utils/maps-link.util';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface RouteAnchor {
  latitude: number;
  longitude: number;
  source: 'live_gps' | 'manual';
}

interface RouteStop {
  visitId: string;
  sequenceIndex: number;
  targetLocation: { latitude: number; longitude: number; address?: string };
  legDistanceMeters?: number;
  legDurationSeconds?: number;
  cumulativeDistanceMeters?: number;
  cumulativeDurationSeconds?: number;
}

interface RoutePlan {
  anchor: { latitude: number; longitude: number; label?: string; source: 'live_gps' | 'manual' };
  mode: MapsDirectionsMode;
  engine: 'haversine' | 'osrm';
  engineFallbackReason?: string;
  stops: RouteStop[];
  skippedVisitIds: string[];
  totalDistanceMeters?: number;
  totalDurationSeconds?: number;
  computedAt: string;
}

const MODES: MapsDirectionsMode[] = ['driving', 'walking', 'cycling', 'motorcycle'];

const MODE_LABEL_KEYS = {
  driving: 'visits.route.modeDriving',
  walking: 'visits.route.modeWalking',
  cycling: 'visits.route.modeCycling',
  motorcycle: 'visits.route.modeMotorcycle',
} as const;

function formatDistance(meters?: number): string | null {
  if (meters === undefined || meters === null) return null;
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)}m`;
}

export const VisitRouteScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Entered from a specific campaign's detail screen (Issue: routes should
  // be available at the campaign level too) — pre-filters the visit set to
  // that campaign, but otherwise behaves identically (still select-all-by-
  // default, still deselectable).
  const campaignId: string | undefined = routeParams?.campaignId;

  const [candidateVisits, setCandidateVisits] = useState<Visit[]>([]);
  const [isLoadingVisits, setIsLoadingVisits] = useState(false);
  const [selectedVisitIds, setSelectedVisitIds] = useState<string[]>([]);

  const [anchorChoice, setAnchorChoice] = useState<'current' | 'manual'>('current');
  const [anchor, setAnchor] = useState<RouteAnchor | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');

  const [mode, setMode] = useState<MapsDirectionsMode>('driving');

  const [isComputing, setIsComputing] = useState(false);
  const [routePlan, setRoutePlan] = useState<RoutePlan | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);

  // Routes across ALL of the caller's eligible visits (scoped server-side by
  // visits.view — own+department at view/edit, everything at admin — the
  // SAME listVisits endpoint every other visits screen uses, no separate
  // "today" narrowing), optionally pre-filtered to one campaign. Confirmed
  // real bug this replaces: the old 'custom' mode hardcoded assignedTo:
  // user.id client-side, which both duplicated and fought with the server's
  // OWN access scope, and a separate 'today' mode had no visible visit list
  // at all (server picked "today" opaquely) — neither could show visits
  // across all campaigns or let the user pick a subset with everything
  // selected by default.
  useEffect(() => {
    const load = async () => {
      setIsLoadingVisits(true);
      try {
        const filter: Record<string, any> = { status: 'scheduled,pending' };
        if (campaignId) filter.campaignId = campaignId;
        const res = await apiService.listVisits(filter);
        if (Array.isArray(res.data)) {
          setCandidateVisits(res.data);
          // Select-all by default — the user deselects what they don't
          // want, rather than starting from nothing and hand-picking.
          setSelectedVisitIds(res.data.map((v) => v._id));
        }
      } finally {
        setIsLoadingVisits(false);
      }
    };
    load();
  }, [campaignId]);

  const allSelected = candidateVisits.length > 0 && selectedVisitIds.length === candidateVisits.length;
  const toggleSelectAll = () => {
    setSelectedVisitIds(allSelected ? [] : candidateVisits.map((v) => v._id));
  };

  const fetchCurrentLocation = async () => {
    setIsFetchingLocation(true);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        setErrorMessage(t('visits.execute.errors.locationPermissionDenied'));
        return;
      }
      const location = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
      setAnchor({ latitude: location.coords.latitude, longitude: location.coords.longitude, source: 'live_gps' });
    } catch (err) {
      console.warn('[VisitRoute] fetchCurrentLocation error:', err);
      setErrorMessage(t('visits.execute.errors.locationFailed'));
    } finally {
      setIsFetchingLocation(false);
    }
  };

  const selectCurrentLocation = () => {
    setAnchorChoice('current');
    if (!anchor || anchor.source !== 'live_gps') fetchCurrentLocation();
  };

  const selectManualAnchor = () => {
    setAnchorChoice('manual');
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (!isNaN(lat) && !isNaN(lng)) {
      setAnchor({ latitude: lat, longitude: lng, source: 'manual' });
    } else {
      setAnchor(null);
    }
  };

  const onManualCoordsChange = (lat: string, lng: string) => {
    setManualLat(lat);
    setManualLng(lng);
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    if (anchorChoice === 'manual') {
      setAnchor(!isNaN(parsedLat) && !isNaN(parsedLng) ? { latitude: parsedLat, longitude: parsedLng, source: 'manual' } : null);
    }
  };

  const toggleVisitSelected = (visitId: string) => {
    setSelectedVisitIds((prev) => (prev.includes(visitId) ? prev.filter((id) => id !== visitId) : [...prev, visitId]));
  };

  const canCompute = !!anchor && selectedVisitIds.length > 0;

  const handleCompute = async () => {
    if (!anchor) {
      setErrorMessage(t('visits.route.noAnchor'));
      return;
    }
    if (selectedVisitIds.length === 0) {
      setErrorMessage(t('visits.route.noVisitsSelected'));
      return;
    }
    setErrorMessage(null);
    setIsComputing(true);
    setRoutePlan(null);
    try {
      const res = await apiService.computeVisitRoute({
        anchor,
        mode,
        visitIds: selectedVisitIds,
      });
      if (res.data) {
        setRoutePlan(res.data as RoutePlan);
      } else {
        setErrorMessage(res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      console.warn('[VisitRoute] compute error:', err);
      setErrorMessage(err?.message || t('visits.errors.generic'));
    } finally {
      setIsComputing(false);
    }
  };

  const openStopInMaps = (stop: RouteStop, index: number) => {
    const origin = index === 0 ? anchor ?? undefined : routePlan?.stops[index - 1]?.targetLocation;
    const url = buildMapsUrl(stop.targetLocation, { origin, mode });
    Linking.openURL(url);
  };

  const totalDistanceLabel = formatDistance(routePlan?.totalDistanceMeters);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={campaignId ? t('visits.route.titleForCampaign') : t('visits.route.title')} onBack={() => navigateTo(campaignId ? 'visit-campaign-detail' : 'visits', undefined, campaignId ? { campaignId } : undefined)} />}
    >
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.section}>
          <View style={styles.selectAllRow}>
            <Text style={styles.sectionLabel}>
              {t('visits.route.visitsCount', { selected: selectedVisitIds.length, total: candidateVisits.length })}
            </Text>
            {candidateVisits.length > 0 && (
              <TouchableOpacity onPress={toggleSelectAll}>
                <Text style={styles.selectAllLink}>
                  {allSelected ? t('visits.route.deselectAll') : t('visits.route.selectAll')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
          {isLoadingVisits ? (
            <ActivityIndicator color={colors.primary as string} />
          ) : candidateVisits.length === 0 ? (
            <Text style={styles.checkRowSubtitle}>{t('visits.route.noEligibleVisits')}</Text>
          ) : (
            candidateVisits.map((visit) => {
              const checked = selectedVisitIds.includes(visit._id);
              return (
                <TouchableOpacity key={visit._id} style={styles.checkRow} onPress={() => toggleVisitSelected(visit._id)}>
                  <Ionicons
                    name={checked ? 'checkbox' : 'square-outline'}
                    size={22}
                    color={checked ? (colors.primary as string) : (colors.text.secondary as string)}
                  />
                  <View style={styles.checkRowText}>
                    <Text style={styles.checkRowTitle}>{visit.targetLocation?.address || visit.contactId.slice(-6)}</Text>
                    {!!visit.scheduledFor && <Text style={styles.checkRowSubtitle}>{visit.scheduledFor}</Text>}
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>{t('visits.route.anchorCurrentLocation')}</Text>
          <View style={styles.segmentRow}>
            <TouchableOpacity
              style={[styles.segmentChip, anchorChoice === 'current' && styles.segmentChipActive]}
              onPress={selectCurrentLocation}
            >
              {isFetchingLocation && anchorChoice === 'current' ? (
                <ActivityIndicator size="small" color={colors.primary as string} />
              ) : (
                <Text style={[styles.segmentChipText, anchorChoice === 'current' && styles.segmentChipTextActive]}>
                  {t('visits.route.anchorCurrentLocation')}
                </Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.segmentChip, anchorChoice === 'manual' && styles.segmentChipActive]}
              onPress={selectManualAnchor}
            >
              <Text style={[styles.segmentChipText, anchorChoice === 'manual' && styles.segmentChipTextActive]}>
                {t('visits.route.anchorManual')}
              </Text>
            </TouchableOpacity>
          </View>
          {anchorChoice === 'manual' && (
            <View style={styles.coordRow}>
              <View style={styles.coordField}>
                <Text style={styles.coordLabel}>{t('visits.route.latitude')}</Text>
                <TextInput
                  style={styles.coordInput}
                  keyboardType="numeric"
                  value={manualLat}
                  onChangeText={(v) => onManualCoordsChange(v, manualLng)}
                  placeholder="0.0000"
                  placeholderTextColor={colors.text.secondary as string}
                />
              </View>
              <View style={styles.coordField}>
                <Text style={styles.coordLabel}>{t('visits.route.longitude')}</Text>
                <TextInput
                  style={styles.coordInput}
                  keyboardType="numeric"
                  value={manualLng}
                  onChangeText={(v) => onManualCoordsChange(manualLat, v)}
                  placeholder="0.0000"
                  placeholderTextColor={colors.text.secondary as string}
                />
              </View>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>{t('visits.route.directionsMode')}</Text>
          <View style={styles.segmentRow}>
            {MODES.map((m) => (
              <TouchableOpacity
                key={m}
                style={[styles.segmentChip, mode === m && styles.segmentChipActive]}
                onPress={() => setMode(m)}
              >
                <Text style={[styles.segmentChipText, mode === m && styles.segmentChipTextActive]}>
                  {t(MODE_LABEL_KEYS[m])}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {!!errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

        <TouchableOpacity
          style={[styles.computeButton, !canCompute && styles.computeButtonDisabled]}
          onPress={handleCompute}
          disabled={!canCompute || isComputing}
        >
          {isComputing ? (
            <ActivityIndicator color={colors.white as string} />
          ) : (
            <Text style={styles.computeButtonText}>{t('visits.route.compute')}</Text>
          )}
        </TouchableOpacity>

        {isComputing && (
          <View style={styles.resultsLoading}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        )}

        {!isComputing && routePlan && (
          <View style={styles.section}>
            {!!routePlan.engineFallbackReason && (
              <View style={styles.banner}>
                <Text style={styles.bannerText}>{t('visits.route.fallbackBanner')}</Text>
              </View>
            )}

            {!!totalDistanceLabel && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{t('visits.route.totalDistance')}</Text>
                <Text style={styles.totalValue}>{totalDistanceLabel}</Text>
              </View>
            )}

            {routePlan.stops.map((stop, index) => {
              const legLabel = formatDistance(stop.legDistanceMeters);
              const addressLabel =
                stop.targetLocation.address ||
                `${stop.targetLocation.latitude.toFixed(4)}, ${stop.targetLocation.longitude.toFixed(4)}`;
              return (
                <View key={stop.visitId} style={styles.stopRow}>
                  <View style={styles.stopBadge}>
                    <Text style={styles.stopBadgeText}>{stop.sequenceIndex + 1}</Text>
                  </View>
                  <View style={styles.stopBody}>
                    <Text style={styles.stopAddress}>{addressLabel}</Text>
                    <View style={styles.stopMetaRow}>
                      {!!legLabel && <Text style={styles.stopMeta}>{legLabel}</Text>}
                      {!!stop.legDurationSeconds && (
                        <Text style={styles.stopMeta}>{Math.round(stop.legDurationSeconds / 60)} min</Text>
                      )}
                    </View>
                    <TouchableOpacity style={styles.openMapsButton} onPress={() => openStopInMaps(stop, index)}>
                      <Ionicons name="navigate-outline" size={16} color={colors.primary as string} />
                      <Text style={styles.openMapsButtonText}>{t('visits.route.openInMaps')}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}

            {routePlan.skippedVisitIds.length > 0 && (
              <View style={styles.skippedSection}>
                <TouchableOpacity style={styles.skippedHeader} onPress={() => setShowSkipped((v) => !v)}>
                  <Text style={styles.sectionLabel}>
                    {t('visits.route.notIncluded')} ({routePlan.skippedVisitIds.length})
                  </Text>
                  <Ionicons
                    name={showSkipped ? 'chevron-up' : 'chevron-down'}
                    size={18}
                    color={colors.text.secondary as string}
                  />
                </TouchableOpacity>
                {showSkipped && (
                  <View>
                    <Text style={styles.skippedNote}>{t('visits.route.notIncludedNote')}</Text>
                    {routePlan.skippedVisitIds.map((id) => (
                      <Text key={id} style={styles.skippedId}>
                        {id}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  content: { padding: spacing.lg, paddingBottom: spacing.xl, gap: spacing.lg },
  section: { gap: spacing.sm },
  sectionLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary as string },
  selectAllRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  selectAllLink: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  segmentRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  segmentChip: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 16,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  segmentChipActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentChipTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
  checkRowText: { flex: 1 },
  checkRowTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  checkRowSubtitle: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  coordRow: { flexDirection: 'row', gap: spacing.sm },
  coordField: { flex: 1 },
  coordLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.xs },
  coordInput: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    fontSize: typography.fontSize.base,
    color: colors.text.primary as string,
  },

  errorText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.error as string },

  computeButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  computeButtonDisabled: { opacity: 0.5 },
  computeButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white as string },

  resultsLoading: { paddingVertical: spacing.xl, alignItems: 'center' },

  // Warning tint that works in light and dark (a fixed light yellow made
  // the themed text unreadable in dark mode)
  banner: {
    backgroundColor: withAlpha(colors.warning, 0.15),
    borderWidth: 1,
    borderColor: withAlpha(colors.warning, 0.4),
    borderRadius: 8,
    padding: spacing.sm,
  },
  bannerText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },

  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingBottom: spacing.xs },
  totalLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  totalValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },

  stopRow: { flexDirection: 'row', gap: spacing.sm, paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border as string },
  stopBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.white as string },
  stopBody: { flex: 1, gap: spacing.xs },
  stopAddress: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  stopMetaRow: { flexDirection: 'row', gap: spacing.sm },
  stopMeta: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  openMapsButton: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.xs },
  openMapsButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  skippedSection: { marginTop: spacing.sm, gap: spacing.xs },
  skippedHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.xs },
  skippedNote: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  skippedId: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
});
